import { MarkdownASTToDeltaExtension } from '@blocksuite/affine-shared/adapters';

export const markdownTextToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'text',
  match: ast => ast.type === 'text',
  toDelta: ast => {
    if (!('value' in ast)) {
      return [];
    }
    return [{ insert: ast.value }];
  },
});

export const markdownInlineCodeToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'inlineCode',
  match: ast => ast.type === 'inlineCode',
  toDelta: ast => {
    if (!('value' in ast)) {
      return [];
    }
    return [{ insert: ast.value, attributes: { code: true } }];
  },
});

export const markdownStrongToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'strong',
  match: ast => ast.type === 'strong',
  toDelta: (ast, context) => {
    if (!('children' in ast)) {
      return [];
    }
    return ast.children.flatMap(child =>
      context.toDelta(child).map(delta => {
        delta.attributes = { ...delta.attributes, bold: true };
        return delta;
      })
    );
  },
});

export const markdownEmphasisToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'emphasis',
  match: ast => ast.type === 'emphasis',
  toDelta: (ast, context) => {
    if (!('children' in ast)) {
      return [];
    }
    return ast.children.flatMap(child =>
      context.toDelta(child).map(delta => {
        delta.attributes = { ...delta.attributes, italic: true };
        return delta;
      })
    );
  },
});

export const markdownDeleteToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'delete',
  match: ast => ast.type === 'delete',
  toDelta: (ast, context) => {
    if (!('children' in ast)) {
      return [];
    }
    return ast.children.flatMap(child =>
      context.toDelta(child).map(delta => {
        delta.attributes = { ...delta.attributes, strike: true };
        return delta;
      })
    );
  },
});

export const markdownListToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'list',
  match: ast => ast.type === 'list',
  toDelta: () => [],
});

export const markdownHtmlToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'html',
  match: ast => ast.type === 'html',
  toDelta: ast => {
    if (!('value' in ast)) {
      return [];
    }
    return [{ insert: ast.value }];
  },
});

export const markdownImageToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'image',
  match: ast =>
    ast.type === 'image' &&
    (Boolean((ast as any).data?.inTable)),
  toDelta: (ast, context) => {
    if (!('url' in ast)) {
      return [];
    }
    // 仅在表格内启用 inline 图片，表格外交给图片块
    const inTable =
      Boolean((ast as any).data?.inTable) ||
      Boolean((context.options as any)?.inTable);
    if (!inTable) {
      return [];
    }
    const rawUrl = ast.url || '';
    const imageProxy = context.configs.get('imageProxy');
    const proxiedUrl =
      imageProxy &&
        /^https?:\/\//i.test(rawUrl) &&
        !rawUrl.startsWith(imageProxy)
        ? `${imageProxy}${rawUrl}`
        : rawUrl;

    return [
      {
        insert: ' ',
        attributes: {
          image: {
            src: proxiedUrl,
            alt: 'alt' in ast ? (ast.alt ?? '') : '',
            title: 'title' in ast ? (ast.title ?? '') : '',
          },
        },
      },
    ];
  },
});

// Video URL regex: matches common video file extensions
// Must match the regex in blocksuite/affine/inlines/link/src/adapters/markdown/markdown-inline.ts
const videoUrlRegex =
  /https?:\/\/[^\s"']+\.(?:mp4|webm|ogg|mov|m4v|flv|mkv|avi|wmv|3gp)(?:\?[^"'\\s]*)?/i;

export const markdownVideoToDeltaMatcher = MarkdownASTToDeltaExtension({
  name: 'video',
  match: ast => {
    // Match link nodes that point to video URLs
    if (ast.type === 'link' && 'url' in ast) {
      return videoUrlRegex.test((ast as any).url || '');
    }
    return false;
  },
  toDelta: (ast, context) => {
    if (!('url' in ast)) {
      return [];
    }
    const rawUrl = ast.url || '';
    // 仅表格内渲染为 inline 视频，其他位置交给 embed 视频块
    const inTable =
      Boolean((ast as any).data?.inTable) ||
      Boolean((context.options as any)?.inTable);
    if (!inTable) {
      return [];
    }
    if (!videoUrlRegex.test(rawUrl)) {
      return [];
    }
    const imageProxy = context.configs.get('imageProxy');
    const proxiedUrl =
      imageProxy &&
        /^https?:\/\//i.test(rawUrl) &&
        !rawUrl.startsWith(imageProxy)
        ? `${imageProxy}${rawUrl}`
        : rawUrl;

    return [
      {
        insert: ' ',
        attributes: {
          video: {
            src: proxiedUrl,
            alt: 'children' in ast && Array.isArray(ast.children) && ast.children.length > 0
              ? (ast.children[0] as any).value || ''
              : '',
            title: undefined,
          },
        },
      },
    ];
  },
});

export const MarkdownInlineToDeltaAdapterExtensions = [
  markdownTextToDeltaMatcher,
  markdownInlineCodeToDeltaMatcher,
  markdownStrongToDeltaMatcher,
  markdownEmphasisToDeltaMatcher,
  markdownDeleteToDeltaMatcher,
  markdownListToDeltaMatcher,
  markdownHtmlToDeltaMatcher,
  markdownImageToDeltaMatcher,
  // Video matcher must be before link matcher to ensure video links are handled correctly
  markdownVideoToDeltaMatcher,
];
